import { useQuery } from "@tanstack/react-query";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { DocumentsItemDto } from "@/api/types";
import documentEmpty from "@/assets/images/document-empty.svg";
import { Anchor } from "@/components/Anchor/Anchor";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { MoveIcon } from "@/components/Icons/MoveIcon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Pagination } from "@/components/Pagination/Pagination";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { Table } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useScreenIsBiggerThan } from "@/hooks/useScreenIsBiggerThan";
import { useSlug } from "@/hooks/useSlug";
import { DocumentsFileIcon } from "@/modules/documents/components/DocumentsFileIcon";
import { DocumentsMoveItemModal } from "@/modules/documents/components/DocumentsMoveItemModal";
import { documentMutations, useDocumentQueries } from "@/queries/documents";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const ROOT_FOLDER_ID = "root";
const PAGE_SIZE = 10;

export function DocumentsListPage(): React.ReactNode {
  const [page, setPage] = useState(0);
  const [moveItem, setMoveItem] = useState<DocumentsItemDto | undefined>(undefined);

  const { componentProps, openDeleteModal } = useDeleteModal<FolderItemDeleteParam>("document-delete-modal");
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDesktop = useScreenIsBiggerThan("md");
  const sessionUser = useSessionUser();
  const { folderId } = useParams<{
    folderId: string;
  }>();
  const documentQueries = useDocumentQueries();
  const {
    data: documentList,
    isPending: isPendingDocumentList,
    error: documentListError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: folderId!,
      page,
    }),
    enabled: Boolean(folderId),
  });
  const deleteFolderMutation = documentMutations.useDeleteFolder();
  const deleteFileMutation = documentMutations.useDeleteFile();
  const updateFileViewCountMutation = documentMutations.useUpdateFileViewCount();
  const handleDeleteItem = async ({ id, isFolder }: { id: string; isFolder: boolean }) => {
    if (!folderId) {
      return;
    }
    if (isFolder) {
      await deleteFolderMutation.mutateAsync({
        parentFolderId: folderId,
        folderId: id,
      });

      return;
    }
    await deleteFileMutation.mutateAsync({
      folderId,
      fileId: id,
    });
  };
  const handleOpenDocument = useCallback(
    (id: string) => {
      if (!folderId) {
        return;
      }
      void updateFileViewCountMutation.mutateAsync({
        folderId: folderId,
        fileId: id,
      });
    },
    [folderId, updateFileViewCountMutation],
  );
  const hiddenColumns = {
    createdAt: isDesktop,
  };
  const columns = useMemo(() => {
    const helper = createColumnHelper<Omit<DocumentsItemDto, "parent">>();

    return [
      helper.accessor("name", {
        header: t("page.document.list.columns.name.title"),
        cell: (cell) => {
          const itemName = (
            <div className="inline-flex max-w-[500px] items-center gap-2">
              <span className="shrink-0">
                <DocumentsFileIcon type={cell.row.original.type} />
              </span>
              <span className="truncate pr-0.5 text-body-bold">{cell.getValue()}</span>
            </div>
          );

          return cell.row.original.type === "folder" ? (
            <Anchor
              isBold
              style="inherit"
              data-testid={`folder_${cell.getValue()}`}
              to={routes.documents.documentsList({
                slug,
                folderId: cell.row.original.id,
              })}
            >
              {itemName}
            </Anchor>
          ) : (
            <span>{cell.getValue()}</span>
          );
        },
      }),
      helper.accessor("createdAt", {
        header: t("page.document.list.columns.created.title"),
        cell: (cell) => <FormattedDate date={cell.getValue()} format="dateRelative" />,
      }),
      helper.accessor("document", {
        header: t("page.document.list.columns.file.title"),
        cell: (cell) => {
          const value = cell.getValue();

          return value ? (
            <a
              className="break-all text-aop-basic-blue-500 hover:underline"
              data-testid={`file_${cell.row.original.name}`}
              href={value?.url}
              target="_blank"
              onClick={() => handleOpenDocument(cell.row.original.id)}
              rel="noopener noreferrer"
            >
              {value?.fileName || value.url}
            </a>
          ) : (
            <span>-</span>
          );
        },
      }),
      ...(sessionUser.isAdmin
        ? [
            helper.accessor("viewCount", {
              header: () => (
                <Tooltip tooltip={t("common.tooltip.view-count-disclaimer")}>
                  <div className="flex items-center gap-1">
                    {t("page.document.list.columns.view.title")}
                    <InfoCircleIcon size={16} className="text-grey-700" />
                  </div>
                </Tooltip>
              ),
              cell: (cell) => {
                return <span>{cell.row.original.type !== "folder" ? cell.getValue() : "-"}</span>;
              },
            }),
          ]
        : []),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const id = cell.getValue();
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("page.document.list.action.edit"),
                disabled: t("model.document.folder.action.edit.not-allowed"),
              },
              "data-testid": "context-menu-edit-btn",
              value: "edit",
              icon: <Edit05Icon />,
              callbackFn: () => {
                if (cell.row.original.type === "folder") {
                  void navigate(
                    routes.documents.folderEdit({
                      slug,
                      folderId: id,
                    }),
                  );
                } else {
                  void navigate(
                    routes.documents.fileEdit({
                      slug,
                      folderId: folderId!,
                      fileId: id,
                    }),
                  );
                }
              },
              isDisabled: !cell.row.original.canEdit,
            },
            {
              labels: {
                default: t("page.document.list.action.move"),
                disabled: t("model.document.folder.action.move.not-allowed"),
              },
              value: "move",
              icon: <MoveIcon />,
              callbackFn: () => setMoveItem(cell.row.original as DocumentsItemDto),
              isDisabled: !cell.row.original.canMove,
            },
            {
              labels: {
                default: t("common.action.delete"),
                disabled: t("model.document.folder.action.delete.not-allowed"),
              },
              "data-testid": "context-menu-delete-btn",
              value: "delete",
              icon: <Trash02Icon />,
              callbackFn: () => {
                openDeleteModal({
                  id,
                  isFolder: cell.row.original.type === "folder",
                });
              },
              isDisabled: !cell.row.original.canDelete,
            },
          ];
          if (!menuActions.length) {
            return null;
          }

          return (
            <div className="flex justify-end">
              <ContextMenu items={menuActions} />
            </div>
          );
        },
      }),
    ];
  }, [t, sessionUser.isAdmin, slug, handleOpenDocument, navigate, folderId, openDeleteModal]);

  const folderTable = useReactTable({
    columns,
    data: documentList?.items ?? [],
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      columnVisibility: hiddenColumns,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const canAddItems = documentList?.canAddNew || false;
  const totalPages = documentList ? Math.ceil(documentList.total / PAGE_SIZE) : 0;

  if (!folderId) {
    return <ErrorPage status={404} />;
  }

  if (documentList == null) {
    return null;
  }

  if (documentListError) {
    return <ErrorPage error={documentListError} />;
  }

  if (isPendingDocumentList) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <DocumentPaper
      title={documentList.name}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("model.document.breadcrumbs.root"),
              id: ROOT_FOLDER_ID,
            },
            ...documentList.ancestors,
            {
              name: documentList.name,
              id: folderId,
            },
          ].map(({ name, id: currFolderId }) => ({
            to:
              currFolderId === ROOT_FOLDER_ID
                ? routes.documents.rootList({
                    slug,
                  })
                : routes.documents.documentsList({
                    slug,
                    folderId: currFolderId,
                  }),
            name,
          }))}
        />
      }
      actions={
        canAddItems ? (
          <div className="flex gap-4">
            <Button
              type="link"
              data-testid="documents-folder-create"
              styling="secondary"
              icon={<PlusIcon />}
              href={routes.documents.folderCreate({
                slug,
                folderId,
              })}
            >
              {t("page.document.list.action.new-folder")}
            </Button>
            <Button
              type="link"
              data-testid="documents-file-upload"
              href={routes.documents.fileCreate({
                slug,
                folderId,
              })}
            >
              {t("page.document.list.action.new-item")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col">
        {documentList.items.length ? (
          <div>
            <Table
              table={folderTable}
              getRowProps={() => ({
                "data-testid": "documents-folder-list-item",
              })}
            />
            <Pagination count={totalPages} currentIndex={page} onChange={setPage} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center overflow-hidden rounded-lg bg-white py-24 text-center">
            <img className="mb-6 w-80 max-w-full" src={documentEmpty} alt="" role="presentation" />
            <h2 className="text-body-bold">{t("page.document.root.list.empty.title")}</h2>
            <p className="text-grey-700">{t("page.document.root.list.empty.description")}</p>
          </div>
        )}
        <DocumentsMoveItemModal
          folderId={folderId}
          item={moveItem}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setMoveItem(undefined);
            }
          }}
          isOpened={Boolean(moveItem)}
        />
        <DeleteModal<FolderItemDeleteParam>
          title={
            componentProps.modalContext?.isFolder
              ? t("model.document.folder.action.delete.confirmation")
              : t("model.document.file.action.delete.confirmation")
          }
          description={
            componentProps.modalContext?.isFolder
              ? t("model.document.folder.action.delete.description")
              : t("model.document.file.action.delete.description")
          }
          onDelete={handleDeleteItem}
          {...componentProps}
        />
      </div>
    </DocumentPaper>
  );
}
type FolderItemDeleteParam = {
  id: string;
  isFolder: boolean;
};
